import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { AlertTriangle, CheckCircle2, SlidersHorizontal, Search, ChevronDown, ChevronUp, X, Filter, BookOpen, Ticket, User, MessageSquare, RefreshCw, Clock as ClockIcon, TrendingUp, TrendingDown, Sparkles, Camera, Download } from 'lucide-react';
import { toast } from 'sonner';
import { ZohoCustomerLink, ZohoInvoiceLink, ZohoCrmLeadLink, EnrollmentNoCrmLink, ZohoBooksCustomerButton } from '../../components/ZohoLinks';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { BookmarkInline, BookmarkMapProvider, useBookmarkMap, bookmarkRowClass } from '../../components/BookmarkButton';

const RULES = [
  { id: 'paid_no_batch', label: 'Admission Pending ( Paid <30% and Not in CreditNote )', desc: 'Students who paid above threshold but are not assigned to any batch and have no applied credit notes', hasThreshold: true, thresholdLabel: 'Min Paid %', defaultThreshold: 30 },
  { id: 'paid_no_active_batch', label: 'Paid but No Active Batch', desc: 'Students who paid above threshold but not in any active/upcoming batch', hasThreshold: true, thresholdLabel: 'Min Paid %', defaultThreshold: 30 },
  { id: 'in_batch_low_paid', label: 'In Batch but Underpaid', desc: 'Students enrolled in a batch but have paid less than threshold % of total fees', hasThreshold: true, thresholdLabel: 'Max Paid %', defaultThreshold: 30 },
  { id: 'in_batch_zero_paid', label: 'In Batch but Zero Paid', desc: 'Students enrolled in a batch but have paid nothing', hasThreshold: false },
  { id: 'overdue_completed_batch', label: 'Overdue in Completed Batch', desc: 'Students with dues who are only in completed batches', hasThreshold: false },
  { id: 'overdue_invoice_days', label: 'Overdue Invoices > N days (Zoho Books)', desc: 'Students with at least one Zoho Books invoice whose due-date is older than the threshold number of days and still has a balance', hasThreshold: true, thresholdLabel: 'Min Days Overdue', defaultThreshold: 45, thresholdType: 'days' },
  { id: 'high_writeoff', label: 'High Write-Off', desc: 'Students with write-off amount above threshold', hasThreshold: true, thresholdLabel: 'Min Write-Off Amount', defaultThreshold: 0, thresholdType: 'amount' },
];

const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '-';

function ZohoSyncBadge({ sync, highlight, onSynced }) {
  const [syncing, setSyncing] = React.useState(false);
  const last = sync?.last_sync;
  const finishedAt = last?.finished_at || last?.started_at;
  const ageMin = finishedAt ? Math.round((Date.now() - new Date(finishedAt).getTime()) / 60000) : null;
  const ageLabel = ageMin == null ? '—' : ageMin < 60 ? `${ageMin}m ago` : ageMin < 1440 ? `${Math.round(ageMin / 60)}h ago` : `${Math.round(ageMin / 1440)}d ago`;
  // Stale = older than 6h
  const stale = ageMin != null && ageMin > 360;
  const isRunning = !!sync?.is_running || syncing;
  const triggerSync = async () => {
    if (isRunning) return;
    if (!window.confirm('Trigger a full Zoho Books invoice sync now? This re-pulls all invoices and may take 1-2 minutes.')) return;
    setSyncing(true);
    try {
      await api.post('/training/zoho/sync');
      toast.success('Zoho sync started — refreshing in 8s…');
      setTimeout(async () => {
        try {
          const r = await api.get('/training/zoho/sync-status');
          onSynced?.(r.data);
        } catch (_) {}
        setSyncing(false);
      }, 8000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start sync');
      setSyncing(false);
    }
  };
  const tone = isRunning ? 'bg-violet-50 text-violet-700 border-violet-200' : stale ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-600 border-slate-200';
  const ring = highlight ? 'ring-1 ring-violet-300' : '';
  return (
    <div className={`flex items-center gap-2 ${ring} rounded px-2 py-1`} data-testid="zoho-sync-badge">
      <div className={`flex items-center gap-1 px-2 py-1 rounded border text-[10px] font-medium ${tone}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${isRunning ? 'bg-violet-500 animate-pulse' : stale ? 'bg-amber-500' : 'bg-emerald-500'}`}></span>
        <span>Zoho Books sync · <strong>{isRunning ? 'running…' : ageLabel}</strong></span>
        {last?.synced != null && <span className="text-slate-400">· {last.synced} invoices</span>}
      </div>
      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={triggerSync} disabled={isRunning} data-testid="zoho-sync-now">
        {isRunning ? 'Syncing…' : 'Sync now'}
      </Button>
    </div>
  );
}

function KpiStrip({ kpis, currentTotal, onSnapshotNow }) {
  const [busy, setBusy] = useState(false);
  const trigger = async () => { setBusy(true); try { await onSnapshotNow(); } finally { setBusy(false); } };
  const cards = [
    { id: 'active', label: 'Active Anomalies (current rule)', value: currentTotal, tone: 'rose', icon: AlertTriangle, hint: 'Live data-driven match count' },
    { id: 'tickets', label: 'Open Tickets', value: kpis?.open_tickets, tone: 'amber', icon: Ticket, hint: 'Tickets not yet resolved' },
    { id: 'appeared', label: 'Appeared (last 7 days)', value: kpis?.appeared_7d, tone: 'violet', icon: TrendingUp, hint: 'New anomalies detected' },
    { id: 'cleared', label: 'Auto-Cleared (last 7 days)', value: kpis?.cleared_7d, tone: 'emerald', icon: TrendingDown, hint: 'Data fixed itself' },
    { id: 'median', label: 'Median Time-to-Clear', value: kpis?.median_days_to_clear != null ? `${kpis.median_days_to_clear}d` : '—', tone: 'sky', icon: ClockIcon, hint: 'Across all cleared events' },
  ];
  const tones = {
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    violet: 'bg-violet-50 border-violet-200 text-violet-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    sky: 'bg-sky-50 border-sky-200 text-sky-700',
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="anomaly-kpi-strip">
      {cards.map(c => {
        const Icon = c.icon;
        return (
          <div key={c.id} className={`p-3 rounded-lg border ${tones[c.tone]}`} data-testid={`anomaly-kpi-${c.id}`}>
            <div className="flex items-center justify-between mb-1">
              <Icon className="w-3.5 h-3.5 opacity-80" />
              {c.id === 'median' && (
                <button onClick={trigger} disabled={busy} title="Re-snapshot anomalies now" className="text-[9px] uppercase tracking-wider hover:underline disabled:opacity-50" data-testid="snapshot-now">
                  <Camera className="w-3 h-3 inline -mt-0.5" /> {busy ? '…' : 'Snapshot'}
                </button>
              )}
            </div>
            <p className="text-xl font-bold tabular-nums">{c.value ?? '—'}</p>
            <p className="text-[10px] font-medium leading-tight mt-0.5 opacity-90">{c.label}</p>
            <p className="text-[9px] opacity-60 mt-0.5">{c.hint}</p>
          </div>
        );
      })}
    </div>
  );
}

const EVENT_META = {
  anomaly_appeared: { label: 'Appeared', icon: AlertTriangle, color: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  anomaly_cleared: { label: 'Auto-Cleared', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  ticket_raised: { label: 'Ticket Raised', icon: Ticket, color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  ticket_resolved: { label: 'Ticket Resolved', icon: CheckCircle2, color: 'bg-violet-100 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
  ticket_reopened: { label: 'Ticket Reopened', icon: RefreshCw, color: 'bg-sky-100 text-sky-700 border-sky-200', dot: 'bg-sky-500' },
};

const STATUS_BADGE = {
  active: { label: 'Active', color: 'bg-rose-50 text-rose-700 border-rose-200', icon: AlertTriangle },
  cleared: { label: 'Auto-Cleared', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
};

function HistoryTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ rule: '', status: '', student_search: '', date_from: '', date_to: '' });
  const [expandedKey, setExpandedKey] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    params.append('limit', '500');
    api.get(`/training/reports/anomalies/history?${params}`)
      .then(r => setRows(r.data.lifecycles || []))
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const cols = ['enrollment_no', 'name', 'course', 'counsellor', 'rule_label', 'current_status', 'first_appeared_at', 'last_activity_at', 'event_count', 'ticket_count', 'open_ticket_count'];
    const escape = (v) => v == null ? '' : /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v);
    const csv = [cols.join(','), ...rows.map(r => cols.map(c => escape(r[c])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `anomaly-history-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const fmtAge = (start, end) => {
    if (!start) return '—';
    const s = new Date(start);
    const e = end ? new Date(end) : new Date();
    const days = Math.round((e - s) / 86400000);
    if (days < 1) return '< 1 day';
    if (days === 1) return '1 day';
    return `${days} days`;
  };
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="space-y-3" data-testid="history-tab">
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-56">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filters.student_search} onChange={e => setFilters(f => ({ ...f, student_search: e.target.value }))} placeholder="Enrol / name…" className="h-8 text-xs pl-7" data-testid="history-search" />
          </div>
          <Select value={filters.rule || 'all'} onValueChange={v => setFilters(f => ({ ...f, rule: v === 'all' ? '' : v }))}>
            <SelectTrigger className="h-8 text-xs w-52" data-testid="history-rule"><SelectValue placeholder="All rules" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All rules</SelectItem>
              {RULES.map(r => <SelectItem key={r.id} value={r.id} className="text-xs">{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status || 'all'} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
            <SelectTrigger className="h-8 text-xs w-40" data-testid="history-status"><SelectValue placeholder="All status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All status</SelectItem>
              <SelectItem value="active" className="text-xs">Active</SelectItem>
              <SelectItem value="cleared" className="text-xs">Auto-Cleared</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} className="h-8 text-xs w-36" data-testid="history-date-from" />
          <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} className="h-8 text-xs w-36" data-testid="history-date-to" />
          {(filters.rule || filters.status || filters.student_search || filters.date_from || filters.date_to) && (
            <Button variant="ghost" size="sm" className="h-8 text-[11px] text-rose-600" onClick={() => setFilters({ rule: '', status: '', student_search: '', date_from: '', date_to: '' })} data-testid="history-reset">
              <X className="w-3 h-3 mr-1" />Reset
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2 text-[10px] text-slate-500">
            <Button variant="outline" size="sm" onClick={exportCsv} className="h-8 text-[11px]" data-testid="history-export"><Download className="w-3 h-3 mr-1" />CSV</Button>
            <span><strong className="text-slate-700">{rows.length}</strong> lifecycle{rows.length === 1 ? '' : 's'}</span>
          </div>
        </div>
      </Card>

      {loading ? <p className="text-xs text-slate-500 italic py-6 text-center">Loading…</p> : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 380px)' }}>
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-slate-50 shadow-sm z-10">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b w-8"></th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Enrol</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Name</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Rule</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Counsellor</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Status</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">First Appeared</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Last Activity</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Age</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Tickets</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Events</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const sb = STATUS_BADGE[r.current_status] || STATUS_BADGE.active;
                  const SbIcon = sb.icon;
                  const expanded = expandedKey === r.key;
                  return (
                    <React.Fragment key={r.key}>
                      <tr className={`border-b border-slate-100 hover:bg-violet-50/30 cursor-pointer ${expanded ? 'bg-violet-50/40' : ''}`} onClick={() => setExpandedKey(expanded ? null : r.key)} data-testid={`lifecycle-row-${r.enrollment_no}-${r.rule}`}>
                        <td className="px-3 py-1.5 text-center">{expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}</td>
                        <td className="px-3 py-1.5 font-mono">{r.enrollment_no || '—'}</td>
                        <td className="px-3 py-1.5">{r.name || '—'}</td>
                        <td className="px-3 py-1.5 text-[10px] text-slate-600">{r.rule_label}</td>
                        <td className="px-3 py-1.5 text-[10px] text-slate-600">{r.counsellor || '—'}</td>
                        <td className="px-3 py-1.5"><Badge variant="outline" className={`text-[10px] ${sb.color}`}><SbIcon className="w-3 h-3 mr-1" />{sb.label}</Badge></td>
                        <td className="px-3 py-1.5 text-[10px] text-slate-500 whitespace-nowrap">{fmtTime(r.first_appeared_at)}</td>
                        <td className="px-3 py-1.5 text-[10px] text-slate-500 whitespace-nowrap">{fmtTime(r.last_activity_at)}</td>
                        <td className="px-3 py-1.5 text-[10px] text-slate-600">{fmtAge(r.first_appeared_at, r.current_status === 'cleared' ? r.last_activity_at : null)}</td>
                        <td className="px-3 py-1.5 text-right text-[10px]">
                          {r.ticket_count > 0 ? (
                            <span className="inline-flex items-center gap-1">
                              <Ticket className="w-3 h-3 text-amber-500" />
                              <strong>{r.ticket_count}</strong>
                              {r.open_ticket_count > 0 && <Badge variant="outline" className="text-[8px] bg-amber-50 text-amber-700 border-amber-200 ml-0.5 px-1 py-0">{r.open_ticket_count} open</Badge>}
                            </span>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-right text-[10px] tabular-nums">{r.event_count}</td>
                      </tr>
                      {expanded && (
                        <tr><td colSpan={11} className="bg-slate-50/70 p-0">
                          <LifecycleDrilldown studentId={r.student_id} ruleId={r.rule} />
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && <tr><td colSpan={11} className="text-center text-xs italic text-slate-400 py-6">No lifecycles match the current filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function LifecycleDrilldown({ studentId, ruleId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/training/reports/anomalies/history/${studentId}/${ruleId}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load lifecycle'))
      .finally(() => setLoading(false));
  }, [studentId, ruleId]);
  if (loading) return <p className="text-xs italic text-slate-400 py-4 text-center">Loading lifecycle…</p>;
  if (!data) return null;
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
  const student = data.student || {};
  return (
    <div className="px-6 py-4 border-l-2 border-violet-300">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-white p-3 rounded border">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Student</p>
          <p className="text-sm font-semibold">{student.name}</p>
          <p className="text-[10px] text-slate-500 font-mono">{student.enrollment_no}</p>
          <p className="text-[10px] text-slate-500 mt-1">{student.phone}</p>
          <p className="text-[10px] text-slate-500">{student.email}</p>
          <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
            <a href={`/training/students?search=${encodeURIComponent(student.enrollment_no || '')}`} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-1"><BookOpen className="w-3 h-3" />Student profile</a>
            {student.zoho_customer_id && <a href={`https://books.zoho.com/app/690252559#/contacts/${student.zoho_customer_id}`} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-1">Open in Zoho Books<X className="w-3 h-3 rotate-45" /></a>}
          </div>
        </div>
        <div className="bg-white p-3 rounded border">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Rule</p>
          <p className="text-sm font-semibold">{data.rule_label}</p>
          <p className="text-[10px] text-slate-500 font-mono mt-1">{data.rule}</p>
          <p className="text-[10px] text-slate-500 mt-1">Course: {student.course || '—'}</p>
          <p className="text-[10px] text-slate-500">Counsellor: {student.salesperson || '—'}</p>
        </div>
        <div className="bg-white p-3 rounded border">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Tickets</p>
          {data.tickets.length === 0 ? (
            <p className="text-xs italic text-slate-400">No tickets raised</p>
          ) : (
            <div className="space-y-1.5">
              {data.tickets.map(t => (
                <div key={t.id} className="text-[10px] flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                  <div>
                    <p className="font-medium text-slate-700">{t.assigned_to}</p>
                    <p className="text-slate-500">Raised {fmtTime(t.created_at)}</p>
                  </div>
                  <Badge variant="outline" className={`text-[9px] ${t.status === 'resolved' ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{t.status}</Badge>
                </div>
              ))}
              <a href="/training/tickets" target="_blank" rel="noopener noreferrer" className="text-[10px] text-violet-600 hover:underline inline-flex items-center gap-1 mt-1"><Ticket className="w-3 h-3" />View in Tickets</a>
            </div>
          )}
        </div>
      </div>

      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Timeline ({data.events.length} event{data.events.length === 1 ? '' : 's'})</p>
      <div className="bg-white rounded border p-4">
        <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4">
          {data.events.map((ev) => {
            const meta = EVENT_META[ev.event_type] || { label: ev.event_type, icon: AlertTriangle, color: 'bg-slate-100 text-slate-700 border-slate-200', dot: 'bg-slate-400' };
            const Icon = meta.icon;
            return (
              <li key={ev.id} className="ml-4">
                <span className={`absolute -left-[7px] w-3 h-3 rounded-full border-2 border-white ${meta.dot}`}></span>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={`text-[10px] ${meta.color}`}><Icon className="w-3 h-3 mr-1" />{meta.label}</Badge>
                  <span className="text-[10px] text-slate-500">{fmtTime(ev.created_at)}</span>
                  <span className="text-[10px] text-slate-400">· by {ev.actor}</span>
                  {ev.ticket_id && <a href="/training/tickets" target="_blank" rel="noopener noreferrer" className="text-[10px] text-violet-600 hover:underline inline-flex items-center gap-1 ml-auto">Open ticket<Ticket className="w-3 h-3" /></a>}
                </div>
                {(ev.note || ev.snapshot?.reason) && <p className="text-[11px] text-slate-700 mt-1">{ev.note || ev.snapshot?.reason}</p>}
                {ev.snapshot && Object.keys(ev.snapshot).filter(k => k !== 'reason').length > 0 && (
                  <div className="mt-1 inline-flex flex-wrap gap-2 text-[10px] text-slate-500">
                    {Object.entries(ev.snapshot).filter(([k]) => k !== 'reason' && k !== 'note').map(([k, v]) => v != null && (
                      <span key={k} className="bg-slate-50 px-1.5 py-0.5 rounded"><strong>{k}:</strong> {typeof v === 'number' && k.includes('amount') ? `₹${v.toLocaleString('en-IN')}` : String(v)}</span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

function CounsellorMultiSelect({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const toggle = (val) => {
    const s = new Set(selected);
    if (s.has(val)) s.delete(val); else s.add(val);
    onChange(Array.from(s));
  };
  const label = selected.length === 0 ? 'All Counsellors' : `${selected.length} counsellor${selected.length > 1 ? 's' : ''}`;
  const filtered = searchTerm ? options.filter(c => c.toLowerCase().includes(searchTerm.toLowerCase())) : options;
  return (
    <div className="relative" data-testid="anomaly-counsellor-filter">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 h-9 px-3 border rounded-sm text-xs text-slate-600 bg-white hover:bg-slate-50 min-w-[140px]">
        <User className="w-3 h-3 text-slate-400" />{label}
        {selected.length > 0 && <X className="w-3 h-3 ml-auto text-slate-400 hover:text-red-500" onClick={e => { e.stopPropagation(); onChange([]); }} />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearchTerm(''); }} />
          <div className="absolute z-50 w-56 mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden">
            <div className="p-1.5 border-b">
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search counsellors..." className="w-full h-7 px-2 text-xs border rounded-sm outline-none focus:border-violet-300" autoFocus data-testid="anomaly-counsellor-filter-search" />
            </div>
            <div className="overflow-y-auto max-h-52">
              {filtered.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-3">No matches</p>
              ) : filtered.map(c => (
                <label key={c} className="flex items-center gap-2 px-3 py-1.5 hover:bg-violet-50 cursor-pointer text-xs">
                  <input type="checkbox" checked={selected.includes(c)} onChange={() => toggle(c)} className="rounded border-slate-300" />
                  {c || <span className="italic text-slate-400">(none)</span>}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CourseMultiSelect({ courses, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const toggle = (val) => {
    const s = new Set(selected);
    if (s.has(val)) s.delete(val); else s.add(val);
    onChange(Array.from(s));
  };
  const label = selected.length === 0 ? 'All Courses' : `${selected.length} course${selected.length > 1 ? 's' : ''}`;
  const filtered = searchTerm ? courses.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())) : courses;
  return (
    <div className="relative" data-testid="anomaly-course-filter">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 h-9 px-3 border rounded-sm text-xs text-slate-600 bg-white hover:bg-slate-50 min-w-[120px]">
        <Filter className="w-3 h-3 text-slate-400" />{label}
        {selected.length > 0 && <X className="w-3 h-3 ml-auto text-slate-400 hover:text-red-500" onClick={e => { e.stopPropagation(); onChange([]); }} />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearchTerm(''); }} />
          <div className="absolute z-50 w-56 mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden">
            <div className="p-1.5 border-b">
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search courses..." className="w-full h-7 px-2 text-xs border rounded-sm outline-none focus:border-violet-300" autoFocus data-testid="anomaly-course-filter-search" />
            </div>
            <div className="overflow-y-auto max-h-52">
              {filtered.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-3">No matches</p>
              ) : filtered.map(c => (
                <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-violet-50 cursor-pointer text-xs">
                  <input type="checkbox" checked={selected.includes(c.name)} onChange={() => toggle(c.name)} className="rounded border-slate-300" />
                  {c.name}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function BatchPopover({ enrollment }) {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchBatch = () => {
    if (batch || loading) return;
    setLoading(true);
    api.get(`/training/batches/${enrollment.batch_id}`).then(r => setBatch(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button onClick={fetchBatch} className="text-left text-xs w-full">
          <div className="flex items-center gap-2 border rounded-sm p-2 bg-white hover:bg-violet-50/40 transition-colors cursor-pointer">
            <BookOpen className="w-3 h-3 text-violet-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-medium text-violet-600 hover:underline">#{enrollment.batch_number || '-'}</span>
              <span className="text-slate-500 ml-1.5 truncate">{enrollment.batch_name || enrollment.course_name}</span>
            </div>
            {enrollment.batch_status && (
              <Badge variant="outline" className={`text-[8px] px-1 py-0 flex-shrink-0 ${
                enrollment.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-600' :
                enrollment.batch_status === 'Completed' ? 'bg-slate-50 text-slate-500' :
                enrollment.batch_status === 'On Hold' ? 'bg-amber-50 text-amber-600' :
                'bg-blue-50 text-blue-600'
              }`}>{enrollment.batch_status}</Badge>
            )}
            {enrollment.enrollment_type === 'upgrade' && <Badge variant="outline" className="text-[8px] px-1 py-0 bg-purple-50 text-purple-600">Upgrade</Badge>}
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        {loading ? (
          <div className="flex items-center justify-center py-4"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-500" /></div>
        ) : batch ? (
          <div className="space-y-2 text-xs">
            <p className="font-semibold text-slate-800 text-sm">{batch.name}</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              <span className="text-slate-400">Batch #:</span><span>{batch.batch_number}</span>
              <span className="text-slate-400">Course:</span><span>{batch.course_name || batch.course || '-'}</span>
              <span className="text-slate-400">Trainer:</span><span>{batch.trainer_name || batch.trainer || '-'}</span>
              <span className="text-slate-400">Centre:</span><span>{batch.centre_name || batch.centre || '-'}</span>
              <span className="text-slate-400">Status:</span><span className="font-medium">{batch.batch_status || '-'}</span>
              <span className="text-slate-400">Students:</span><span>{batch.student_count || 0}</span>
              {batch.start_date && <><span className="text-slate-400">Start:</span><span>{batch.start_date}</span></>}
              {batch.end_date && <><span className="text-slate-400">End:</span><span>{batch.end_date}</span></>}
              {(batch.start_time || batch.end_time) && <><span className="text-slate-400">Timing:</span><span>{batch.start_time || ''} - {batch.end_time || ''}</span></>}
            </div>
          </div>
        ) : <p className="text-xs text-slate-400 text-center py-2">No batch data</p>}
      </PopoverContent>
    </Popover>
  );
}

function StudentDetailPanel({ anomaly }) {
  const [enrollments, setEnrollments] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!anomaly?.student_id) return;
    setLoading(true);
    Promise.all([
      api.get(`/training/students?search=${encodeURIComponent(anomaly.enrollment_no || anomaly.name)}&page_size=5`).then(r => {
        const found = (r.data.students || []).find(s => s.id === anomaly.student_id);
        setEnrollments(found?.enrollments || []);
      }).catch(() => setEnrollments([])),
      api.get(`/training/students/${anomaly.student_id}/invoices`).then(r => setInvoices(r.data.invoices || [])).catch(() => setInvoices([])),
    ]).finally(() => setLoading(false));
  }, [anomaly]);

  if (loading) return <div className="py-4 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-violet-500 mx-auto" /></div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-1">
      {/* Contact Info */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Contact Details</p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
          <span className="text-slate-400">Enrollment:</span>
          <span className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium text-slate-700 text-xs">{anomaly.enrollment_no || '-'}</span>
            <ZohoCrmLeadLink enrollmentNo={anomaly.enrollment_no} compact />
            <ZohoBooksCustomerButton zohoCustomerId={anomaly.zoho_customer_id} compact />
          </span>
          <span className="text-slate-400">Phone:</span><span>{anomaly.phone || '-'}</span>
          <span className="text-slate-400">Email:</span><span className="truncate">{anomaly.email || '-'}</span>
          <span className="text-slate-400">Course:</span><span>{anomaly.course || '-'}</span>
          <span className="text-slate-400">Counsellor:</span><span>{anomaly.counsellor || '-'}</span>
        </div>
        {/* Financial Summary */}
        {(anomaly.total_amount > 0) && (
          <div className="mt-3 p-2.5 bg-white border rounded-sm">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Financials</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-slate-500">Total:</span> <span className="font-medium">{fmt(anomaly.total_amount)}</span></div>
              <div><span className="text-slate-500">Paid:</span> <span className="text-emerald-600 font-medium">{fmt(anomaly.paid_amount)}</span></div>
              <div><span className="text-slate-500">Write Off:</span> <span className={(anomaly.write_off_amount || 0) > 0 ? 'text-red-500 font-medium' : 'text-slate-400'}>{(anomaly.write_off_amount || 0) > 0 ? fmt(anomaly.write_off_amount) : '-'}</span></div>
              <div><span className="text-slate-500">Due:</span> <span className="text-red-500 font-medium">{fmt(anomaly.due_amount)}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Enrollments */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Enrollments ({enrollments?.length || 0})</p>
        {!enrollments || enrollments.length === 0 ? (
          <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-sm border border-amber-200">No batch enrollments found</p>
        ) : (
          <div className="space-y-1.5">
            {enrollments.map(e => <BatchPopover key={e.id} enrollment={e} />)}
          </div>
        )}
      </div>

      {/* Invoices */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Invoices ({invoices?.length || 0})</p>
        {!invoices || invoices.length === 0 ? (
          <p className="text-xs text-slate-400">No invoices</p>
        ) : (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {invoices.map((inv, i) => (
              <div key={inv.invoice_id || i} className="border rounded-sm p-2 bg-white text-xs">
                <div className="flex items-center justify-between mb-0.5">
                  <ZohoInvoiceLink invoiceNumber={inv.invoice_number} invoiceId={inv.invoice_id} className="text-xs" />
                  <Badge variant="outline" className={`text-[8px] ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : inv.status === 'overdue' ? 'bg-red-50 text-red-700' : inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{inv.status}</Badge>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-500">
                  {inv.date && <span className="text-slate-400">{inv.date}</span>}
                  <span>Total: {fmt(inv.total)}</span>
                  <span>Paid: <span className="text-emerald-600">{fmt(inv.payment_made)}</span></span>
                  {inv.balance > 0 && <span>Due: <span className="text-red-500">{fmt(inv.balance)}</span></span>}
                  {(inv.write_off_amount || 0) > 0 && <span>W/O: <span className="text-rose-500">{fmt(inv.write_off_amount)}</span></span>}
                </div>
                {inv.due_date && <div className="text-[9px] text-slate-400 mt-0.5">Due date: {inv.due_date}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AnomaliesPage() {
  return (
    <BookmarkMapProvider>
      <AnomaliesPageContent />
    </BookmarkMapProvider>
  );
}

function AnomaliesPageContent() {
  const { map: bookmarkMap } = useBookmarkMap();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('current');
  const [rule, setRule] = useState('paid_no_batch');
  const [threshold, setThreshold] = useState(30);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [writeOffFilter, setWriteOffFilter] = useState('all');
  const [ticketDialog, setTicketDialog] = useState(null);
  const [ticketAssignee, setTicketAssignee] = useState('');
  const [ticketDesc, setTicketDesc] = useState('');
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [courseFilter, setCourseFilter] = useState([]);
  const [courses, setCourses] = useState([]);
  const [counsellorFilter, setCounsellorFilter] = useState([]);
  const [zohoSync, setZohoSync] = useState(null);
  const [flashId, setFlashId] = useState(null);
  const rowRefs = useRef({});

  useEffect(() => {
    api.get('/training/courses').then(r => setCourses(r.data.courses || []));
    api.get('/users').then(r => setUsers(r.data.users || r.data || [])).catch(() => {});
    api.get('/training/zoho/sync-status').then(r => setZohoSync(r.data)).catch(() => {});
    api.get('/training/reports/anomalies/kpis').then(r => setKpis(r.data)).catch(() => {});
  }, []);

  const refreshKpis = () => api.get('/training/reports/anomalies/kpis').then(r => setKpis(r.data)).catch(() => {});

  const currentRule = RULES.find(r => r.id === rule) || RULES[0];

  const fetchAnomalies = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ rule });
    if (currentRule.hasThreshold) {
      if (currentRule.thresholdType === 'amount') {
        params.append('writeoff_threshold', threshold);
      } else if (currentRule.thresholdType === 'days') {
        params.append('overdue_days', threshold);
      } else {
        params.append('threshold', threshold);
      }
    }
    api.get(`/training/reports/anomalies?${params}`)
      .then(r => {
        setAnomalies(r.data.anomalies || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => toast.error('Failed to load anomalies'))
      .finally(() => setLoading(false));
  }, [rule, threshold, currentRule]);

  useEffect(() => { fetchAnomalies(); }, [fetchAnomalies]);

  // Deep-link: ?focus=<enrollment_no>&open=detail|bookmark
  // Auto-expand the matching row, scroll into view, and flash highlight for 2.5s.
  // Once handled, strip the params so back-nav stays clean.
  const focusEnroll = searchParams.get('focus');
  const focusIntent = searchParams.get('open');
  useEffect(() => {
    if (!focusEnroll || loading || anomalies.length === 0) return;
    const target = anomalies.find(a => String(a.enrollment_no) === String(focusEnroll));
    if (!target) {
      // student is not in this rule's results — surface helpful toast
      toast.info(`Student ${focusEnroll} is no longer flagged under this rule`);
      setSearchParams({}, { replace: true });
      return;
    }
    // Clear filters that might hide the row
    setSearch('');
    setCourseFilter([]);
    setWriteOffFilter('all');

    // Expand row + flash
    setExpandedId(target.student_id);
    setFlashId(target.student_id);

    // Scroll into view after render
    setTimeout(() => {
      const el = rowRefs.current[target.student_id];
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);

    // Auto-open bookmark dialog if requested and not already bookmarked
    if (focusIntent === 'bookmark') {
      setTimeout(() => {
        const btn = document.querySelector(`[data-testid="bookmark-inline-${target.enrollment_no}"]`);
        if (btn) btn.click();
      }, 350);
    }

    // Clear the flash after 2.5s and strip URL params
    const t = setTimeout(() => setFlashId(null), 2500);
    setSearchParams({}, { replace: true });
    return () => clearTimeout(t);
  }, [focusEnroll, focusIntent, anomalies, loading, setSearchParams]);

  const handleRuleChange = (newRule) => {
    setRule(newRule);
    const r = RULES.find(x => x.id === newRule);
    if (r?.hasThreshold) setThreshold(r.defaultThreshold);
  };

  const handleRaiseTicket = async () => {
    if (!ticketAssignee) { toast.error('Select an assignee'); return; }
    try {
      await api.post('/training/reports/tickets', {
        student_id: ticketDialog,
        assigned_to: ticketAssignee,
        description: ticketDesc,
        rule: currentRule.label,
        rule_id: currentRule.id,
        source: 'anomaly',
      });
      toast.success('Ticket raised');
      setTicketDialog(null);
      setTicketAssignee('');
      setTicketDesc('');
      refreshKpis();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to raise ticket'); }
  };

  const hasActiveFilters = search || courseFilter.length > 0 || counsellorFilter.length > 0 || writeOffFilter !== 'all';

  const counsellorOptions = React.useMemo(() => {
    return [...new Set(anomalies.map(a => (a.counsellor || '').trim()).filter(Boolean))].sort();
  }, [anomalies]);

  const filtered = anomalies.filter(a => {
    if (courseFilter.length > 0 && !courseFilter.includes(a.course || '')) return false;
    if (counsellorFilter.length > 0 && !counsellorFilter.includes((a.counsellor || '').trim())) return false;
    if (writeOffFilter === 'exclude' && (a.write_off_amount || 0) > 0) return false;
    if (writeOffFilter === 'only' && (a.write_off_amount || 0) <= 0) return false;
    if (search) {
      const s = search.toLowerCase();
      return (a.name || '').toLowerCase().includes(s) || (a.enrollment_no || '').toLowerCase().includes(s) || (a.phone || '').includes(s);
    }
    return true;
  });

  return (
    <div className="space-y-5" data-testid="anomalies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Anomalies</h1>
          <p className="text-sm text-slate-400 mt-0.5">Data-driven detection — anomalies clear automatically when underlying data is fixed</p>
        </div>
      </div>

      {/* KPI Strip */}
      <KpiStrip kpis={kpis} currentTotal={total} onSnapshotNow={async () => {
        try {
          const r = await api.post('/training/reports/anomalies/snapshot-now');
          toast.success(`Snapshot done — appeared ${r.data.appeared}, cleared ${r.data.cleared}`);
          refreshKpis();
          fetchAnomalies();
        } catch (e) { toast.error('Snapshot failed'); }
      }} />

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex items-center gap-1">
          {[{ id: 'current', label: 'Current Anomalies', icon: AlertTriangle }, { id: 'history', label: 'History', icon: ClockIcon }].map(t => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
                className={`px-4 py-2.5 text-xs font-medium border-b-2 -mb-px transition ${active ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                <Icon className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />{t.label}
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === 'history' ? (
        <HistoryTab users={users} courses={courses} />
      ) : (
      <>
      {/* Rule Configuration Panel */}
      <Card className="border p-4" data-testid="anomaly-config">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-violet-500" />
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Detection Rule</p>
          </div>
          <ZohoSyncBadge sync={zohoSync} highlight={rule === 'overdue_invoice_days'} onSynced={(s) => { setZohoSync(s); fetchAnomalies(); }} />
        </div>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[250px]">
            <Select value={rule} onValueChange={handleRuleChange}>
              <SelectTrigger className="h-9 text-xs" data-testid="anomaly-rule-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RULES.map(r => (
                  <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-400 mt-1.5">{currentRule.desc}</p>
          </div>
          {currentRule.hasThreshold && (
            <div className="w-48">
              <label className="text-[10px] text-slate-400 font-semibold uppercase">{currentRule.thresholdLabel}</label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" value={threshold} onChange={e => setThreshold(Number(e.target.value))} className="h-9 text-xs w-24" data-testid="anomaly-threshold" />
                <span className="text-xs text-slate-400">{currentRule.thresholdType === 'amount' ? '₹' : currentRule.thresholdType === 'days' ? 'days' : '%'}</span>
              </div>
            </div>
          )}
          <Button onClick={fetchAnomalies} className="bg-violet-600 hover:bg-violet-700 text-xs rounded-sm h-9 mt-4" data-testid="apply-anomaly-rule">
            Apply
          </Button>
        </div>
      </Card>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by name, enrollment no, phone..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 text-xs rounded-sm" data-testid="anomaly-search" />
        </div>
        <CourseMultiSelect courses={courses} selected={courseFilter} onChange={setCourseFilter} />
        <CounsellorMultiSelect options={counsellorOptions} selected={counsellorFilter} onChange={setCounsellorFilter} />
        <div className="flex items-center border rounded-sm overflow-hidden" data-testid="writeoff-filter">
          {[{ value: 'all', label: 'All' }, { value: 'exclude', label: 'No Write-offs' }, { value: 'only', label: 'Write-offs Only' }].map(opt => (
            <button
              key={opt.value}
              onClick={() => setWriteOffFilter(opt.value)}
              className={`px-2.5 py-1.5 text-[10px] font-medium transition-colors ${writeOffFilter === opt.value ? 'bg-violet-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
              data-testid={`writeoff-filter-${opt.value}`}
            >{opt.label}</button>
          ))}
        </div>
        <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => { fetchAnomalies(); refreshKpis(); }} data-testid="refresh-anomalies">
          <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-9 px-3 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-sm" onClick={() => { setSearch(''); setCourseFilter([]); setCounsellorFilter([]); setWriteOffFilter('all'); }} data-testid="anomaly-clear-filters">
            <X className="w-3.5 h-3.5 mr-1" />Clear Filters
          </Button>
        )}
      </div>

      {/* Results Table */}
      <Card className="border p-0">
        <div className="px-4 py-2.5 border-b bg-slate-50/50 flex items-center gap-2">
          <span className="text-[10px] uppercase text-slate-400 font-semibold">{currentRule.label}</span>
          <span className="text-[10px] text-slate-400">&middot;</span>
          <span className="text-[10px] text-slate-500">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
          <span className="text-[10px] text-slate-300 ml-auto">Click a row to see details</span>
        </div>
        {loading ? (
          <div className="h-32 flex items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle2 className="w-8 h-8 text-emerald-300 mx-auto mb-3" />
            <p className="text-xs text-slate-400">No anomalies found with current rule and threshold.</p>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-[10px] uppercase w-8"></TableHead>
              <TableHead className="text-[10px] uppercase">Enrollment</TableHead>
              <TableHead className="text-[10px] uppercase">Name</TableHead>
              <TableHead className="text-[10px] uppercase">Course</TableHead>
              <TableHead className="text-[10px] uppercase">Counsellor</TableHead>
              <TableHead className="text-[10px] uppercase text-right">Total</TableHead>
              <TableHead className="text-[10px] uppercase text-right">Paid</TableHead>
              <TableHead className="text-[10px] uppercase text-right">Due</TableHead>
              <TableHead className="text-[10px] uppercase text-right">Write-off</TableHead>
              <TableHead className="text-[10px] uppercase">Reason</TableHead>
              <TableHead className="text-[10px] uppercase text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map(a => {
                const bmHighlight = a.enrollment_no ? bookmarkRowClass(a.enrollment_no, bookmarkMap) : '';
                const isFlashing = flashId === a.student_id;
                return (
                <React.Fragment key={a.student_id}>
                  <TableRow
                    ref={el => { if (el) rowRefs.current[a.student_id] = el; }}
                    className={`cursor-pointer hover:bg-violet-50/30 ${expandedId === a.student_id ? 'bg-violet-50/40' : ''} ${bmHighlight} ${isFlashing ? 'ring-2 ring-violet-400 ring-offset-1 transition-all duration-700 bg-violet-100/60' : 'transition-all duration-700'}`}
                    onClick={() => setExpandedId(expandedId === a.student_id ? null : a.student_id)}
                    data-testid={`anomaly-row-${a.student_id}`}
                  >
                    <TableCell className="text-center">{expandedId === a.student_id ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}</TableCell>
                    <TableCell className="text-xs"><EnrollmentNoCrmLink enrollmentNo={a.enrollment_no} /></TableCell>
                    <TableCell className="text-sm font-medium">
                      <div className="flex items-center">
                        <span>{a.name}</span>
                        <BookmarkInline student={{
                          enrollment_no: a.enrollment_no,
                          id: a.student_id,
                          name: a.name,
                          course: a.course || '',
                          due_amount: a.due_amount,
                        }} />
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{a.course || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-500">{a.counsellor || '-'}</TableCell>
                    <TableCell className="text-xs text-right">{fmt(a.total_amount)}</TableCell>
                    <TableCell className="text-xs text-right text-emerald-600 font-medium">{fmt(a.paid_amount)} <span className="text-slate-400">({a.paid_pct}%)</span></TableCell>
                    <TableCell className="text-xs text-right text-red-600 font-medium">{fmt(a.due_amount)}</TableCell>
                    <TableCell className="text-xs text-right">{a.write_off_amount > 0 ? <span className="text-rose-500 font-medium">{fmt(a.write_off_amount)}</span> : <span className="text-slate-300">-</span>}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200 max-w-[180px] truncate" title={a.reason}><AlertTriangle className="w-3 h-3 mr-0.5 flex-shrink-0" />{a.reason}</Badge></TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      {a.open_ticket_id ? (
                        <Button variant="outline" size="sm" className="h-7 text-[10px] text-violet-600 border-violet-200 hover:bg-violet-50" asChild data-testid={`view-ticket-${a.student_id}`}>
                          <a href={`/training/tickets?focus=${a.open_ticket_id}`} title={`Ticket ${a.open_ticket_status} · assigned to ${a.open_ticket_assigned_to || '—'}`}>
                            <Ticket className="w-3 h-3 mr-0.5" />View Ticket
                          </a>
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" className="h-7 text-[10px] text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => { setTicketDialog(a.student_id); setTicketAssignee(''); setTicketDesc(''); }} data-testid={`raise-ticket-${a.student_id}`}>
                          <Ticket className="w-3 h-3 mr-0.5" />Ticket
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {expandedId === a.student_id && (
                    <TableRow key={`${a.student_id}-detail`}>
                      <TableCell colSpan={11} className="bg-slate-50/50 p-4">
                        <StudentDetailPanel anomaly={a} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );})}
            </TableBody>
          </Table>
        )}
      </Card>
      </>
      )}

      {/* Raise Ticket Dialog */}
      <Dialog open={!!ticketDialog} onOpenChange={() => setTicketDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><Ticket className="w-4 h-4 text-amber-500" />Raise Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase text-slate-400 font-semibold block mb-1">Assign To *</label>
              <Select value={ticketAssignee} onValueChange={setTicketAssignee}>
                <SelectTrigger className="h-9 text-xs" data-testid="ticket-assignee-select">
                  <SelectValue placeholder="Select assignee..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id || u.email} value={u.full_name || u.email} className="text-xs">{u.full_name || u.email} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-400 font-semibold block mb-1">Description</label>
              <Textarea placeholder="Describe the issue..." value={ticketDesc} onChange={e => setTicketDesc(e.target.value)} className="text-xs min-h-[60px]" data-testid="ticket-description" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setTicketDialog(null)}>Cancel</Button>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-xs" onClick={handleRaiseTicket} data-testid="confirm-raise-ticket">
              <Ticket className="w-3.5 h-3.5 mr-1" />Raise Ticket
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
